import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { NetworkStatus, useQuery } from "@apollo/client";
import debounce from "lodash/debounce";
import get from "lodash/get";
import { InboxParamsValues } from "@hilos/containers/inbox/InboxParamsForm";
import { ChannelAvailabilityData } from "@hilos/types/channel";
import {
  InboxContactReadV2,
  InboxContactReadV2Query,
} from "@hilos/types/conversation";
import { InboxParams } from "@hilos/types/hilos";
import { CurrentUser } from "@hilos/types/private-schema";
import {
  encodeParams,
  getLimitByCursor,
  getOrderBy,
  getQueryFilters,
  getQueryOrderBy,
  getSubscribeToMoreOptions,
  getVariablesWithCursor,
} from "src/helpers/inbox";
import {
  GET_INBOX_CONTACTS_QUERY,
  getInboxContactCountQuery,
  getInboxContactCountSubscription,
} from "src/helpers/queries";
import { hasItems, isFeatureEnabled } from "src/helpers/utils";

interface useInboxContactsParams {
  session: CurrentUser | null;
  inboxParams: InboxParams;
  setInboxParams: (inboxParams: Partial<InboxParams>) => void;
  isPageVisible: boolean;
  currentAvailableChannels: ChannelAvailabilityData[];
  isLoadingAvailableChannels: boolean;
}

function useInboxContacts({
  session,
  inboxParams,
  setInboxParams,
  isPageVisible,
  currentAvailableChannels,
  isLoadingAvailableChannels,
}: useInboxContactsParams) {
  const { inboxContactId } = useParams();
  const [cursor, setCursor] = useState(null);
  const [allowSubscriptions, setAllowSubscriptions] = useState(false);
  const [hasFetchMore, setHasFetchMore] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isUpdatingFilters, setIsUpdatingFilters] = useState(false);
  const hasPendingRefetch = useRef(false);
  const dataRef = useRef<InboxContactReadV2Query>();
  const lastUpdatedOnRef = useRef<Date | null>(null);
  const lastInboxContactCursor = useRef(null);
  const fetchMoreWithCursorRef = useRef<
    ((cursor: string | null) => void) | null
  >(null);
  const [allowQueryCountWithLimit, setAllowQueryCountWithLimit] = useState(
    isFeatureEnabled({
      session,
      key: "inbox_contact_query_count_with_limit",
    })
  );

  const [
    allowQueryCountSubscription,
    allowSearchByLastMessage,
    allowStreamingSubscription,
  ] = useMemo(
    () => [
      isFeatureEnabled({
        session,
        key: "inbox_contact_count_subscription",
      }),
      isFeatureEnabled({
        session,
        key: "inbox_contact_search_by_last_message",
      }),
      isFeatureEnabled({
        session,
        key: "inbox_contact_streaming_subscription",
      }),
    ],
    [session]
  );

  const { filters, orderBy } = useMemo(() => {
    const nextFilters: { _and?: object[] } = getQueryFilters({
      session,
      inboxParams,
      currentAvailableChannels,
      allowSearchByLastMessage,
    });

    const nextOrderBy = getQueryOrderBy(inboxParams);

    return {
      filters: nextFilters,
      orderBy: nextOrderBy,
    };
  }, [
    session,
    inboxParams,
    currentAvailableChannels,
    allowSearchByLastMessage,
  ]);

  const { data, error, networkStatus, refetch, fetchMore, subscribeToMore } =
    useQuery<InboxContactReadV2Query>(GET_INBOX_CONTACTS_QUERY, {
      variables: { filters, orderBy },
      fetchPolicy: "network-only",
      refetchWritePolicy: "overwrite",
      skip: isLoadingAvailableChannels,
      notifyOnNetworkStatusChange: true,
    });

  const {
    data: inboxContactsCountSubscription,
    loading: loadingCounter,
    subscribeToMore: subscribeToMoreCount,
  } = useQuery(getInboxContactCountQuery(allowQueryCountWithLimit), {
    variables: { filters },
    fetchPolicy: "network-only",
    refetchWritePolicy: "overwrite",
    skip: isLoadingAvailableChannels,
    notifyOnNetworkStatusChange: true,
  });

  const count = useMemo(
    () =>
      inboxContactsCountSubscription?.api_inboxcontact_aggregate?.aggregate
        ?.count || 0,
    [inboxContactsCountSubscription]
  );

  const hasMoreData = useMemo(
    () =>
      hasFetchMore ||
      Boolean(count && (data?.api_inboxcontact?.length ?? 0) < count),
    [count, data, hasFetchMore]
  );

  const [loading, fetching, updating] = useMemo(
    () => [
      NetworkStatus.loading === networkStatus,
      NetworkStatus.fetchMore === networkStatus,
      isUpdatingFilters ||
        [NetworkStatus.setVariables, NetworkStatus.refetch].includes(
          networkStatus
        ),
    ],
    [isUpdatingFilters, networkStatus]
  );

  const handleFetchMore = useCallback((nextCursor) => {
    fetchMoreWithCursorRef.current?.(nextCursor);
  }, []);

  const handleInboxParams = useCallback(
    (nextInboxParams: Partial<InboxParams>) => {
      hasPendingRefetch.current = true;
      setIsUpdatingFilters(true);
      let hasSelectedView = false;
      for (const name in nextInboxParams) {
        if (name) {
          let value = nextInboxParams[name];
          let data: string | null = null;

          if (value && ("filters" !== name || hasItems(value))) {
            if (name === "view" && value) {
              value = value.id;
              hasSelectedView = true;
            }

            data = encodeParams(value);
          }

          if (data) {
            searchParams.set(name, data);
          } else {
            if (searchParams.get(name)) {
              searchParams.delete(name);
            }
          }
        }
      }

      if (hasSelectedView) {
        if (searchParams.get("filters")) {
          searchParams.delete("filters");
        }
        if (searchParams.get("ordering")) {
          searchParams.delete("ordering");
        }
      }

      setSearchParams(searchParams);
      setInboxParams(nextInboxParams);
      setCursor(null);
    },
    [searchParams, setSearchParams, setInboxParams]
  );

  const handleChangeFilters = useCallback(
    async (values: InboxParamsValues) => {
      handleInboxParams({
        ordering: values.ordering,
        filters: values.filters,
        view: values.view,
      });
    },
    [handleInboxParams]
  );

  const handleInboxContactCursor = useCallback(
    debounce((id) => {
      if (lastInboxContactCursor.current !== id) {
        lastInboxContactCursor.current = id;

        if (id) {
          const { field: orderByField } = getOrderBy(orderBy)[0];
          const inboxContact =
            dataRef.current?.api_inboxcontact?.find(
              (inboxContact) => inboxContact.id === id
            ) || null;
          setCursor(inboxContact?.[orderByField] || null);
        } else {
          setCursor(null);
        }
      }
    }, 300),
    [orderBy]
  );

  const handleDisableQueryCountWithLimit = useCallback(() => {
    setAllowQueryCountWithLimit(false);
  }, []);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    setAllowQueryCountWithLimit(
      isFeatureEnabled({
        session,
        key: "inbox_contact_query_count_with_limit",
      })
    );
  }, [session]);

  useEffect(() => {
    fetchMoreWithCursorRef.current = (nextCursor: string | null) => {
      if (!isUpdatingFilters) {
        fetchMore({
          variables: getVariablesWithCursor({
            filters,
            orderBy,
            cursor: nextCursor,
          }),
          updateQuery: (prev, { fetchMoreResult }) => {
            const { field: orderByField, direction: orderDirection } =
              getOrderBy(orderBy)[0];
            const nextComparatorField = get(
              fetchMoreResult,
              `api_inboxcontact[0].${orderByField}`
            );

            if (!nextComparatorField) {
              setHasFetchMore(false);
              return prev;
            }

            let nextHasFetchMore = false;
            const isOrderByDesc = orderDirection !== "asc";
            const nextInboxContacts: InboxContactReadV2[] = [];

            for (const inboxContact of prev.api_inboxcontact) {
              if (
                inboxContact[orderByField] && isOrderByDesc
                  ? inboxContact[orderByField] > nextComparatorField
                  : inboxContact[orderByField] < nextComparatorField
              ) {
                nextInboxContacts.push(inboxContact);
              }
            }

            for (const index in fetchMoreResult.api_inboxcontact) {
              if (Number(index) < 10) {
                nextInboxContacts.push(fetchMoreResult.api_inboxcontact[index]);
              } else {
                nextHasFetchMore = true;
                break;
              }
            }

            setHasFetchMore(nextHasFetchMore);

            return { api_inboxcontact: nextInboxContacts };
          },
        });
      }
    };
  }, [isUpdatingFilters, filters, orderBy, fetchMore]);

  useEffect(() => {
    if (
      networkStatus !== NetworkStatus.ready ||
      isUpdatingFilters ||
      !allowSubscriptions
    ) {
      return;
    }

    if (!lastUpdatedOnRef.current) {
      lastUpdatedOnRef.current = new Date();
    }

    const limitByCursor: string | null = getLimitByCursor(
      dataRef.current,
      lastInboxContactCursor.current
    );

    const subscription = subscribeToMore(
      getSubscribeToMoreOptions({
        allowStreamingSubscription,
        limitByCursor,
        filters,
        orderBy,
        cursor,
        inboxContactId,
        lastUpdatedOn: lastUpdatedOnRef.current,
      })
    );

    return () => {
      lastUpdatedOnRef.current = new Date();
      subscription();
    };
  }, [
    networkStatus,
    isUpdatingFilters,
    allowSubscriptions,
    allowStreamingSubscription,
    error,
    cursor,
    filters,
    orderBy,
    inboxContactId,
    subscribeToMore,
  ]);

  useEffect(() => {
    if (
      loadingCounter ||
      isUpdatingFilters ||
      networkStatus !== NetworkStatus.ready ||
      !allowSubscriptions ||
      !allowQueryCountSubscription
    ) {
      return;
    }

    const subscription = subscribeToMoreCount({
      document: getInboxContactCountSubscription(allowQueryCountWithLimit),
      variables: { filters },
      updateQuery: (prev, { subscriptionData }) => subscriptionData.data,
    });

    return () => {
      subscription();
    };
  }, [
    loadingCounter,
    networkStatus,
    isUpdatingFilters,
    allowSubscriptions,
    allowQueryCountWithLimit,
    allowQueryCountSubscription,
    filters,
    subscribeToMoreCount,
  ]);

  useEffect(() => {
    if (hasPendingRefetch.current) {
      refetch();
      hasPendingRefetch.current = false;
    }
  }, [filters, orderBy, refetch]);

  useEffect(() => {
    if (networkStatus === NetworkStatus.refetch) {
      setIsUpdatingFilters(false);
    }
  }, [networkStatus]);

  useEffect(() => {
    if (!isPageVisible) {
      const applyAllowSubscritionsUpdate = setTimeout(() => {
        setAllowSubscriptions(false);
      }, 5000);

      return () => clearTimeout(applyAllowSubscritionsUpdate);
    }

    if (allowStreamingSubscription && isPageVisible) {
      refetch();
    }

    setAllowSubscriptions(true);
  }, [refetch, isPageVisible, allowStreamingSubscription]);

  const orderByField = useMemo(() => getOrderBy(orderBy)[0].field, [orderBy]);

  return {
    data,
    count,
    error,
    loading,
    updating,
    fetching,
    loadingCounter,
    hasMoreData,
    orderByField,
    inboxContactId,
    allowQueryCountWithLimit,
    setIsUpdatingFilters,
    handleFetchMore,
    handleInboxParams,
    handleChangeFilters,
    handleInboxContactCursor,
    handleDisableQueryCountWithLimit,
  };
}

export default useInboxContacts;
